import { useState } from 'react';
import { billingAdminApi, taxAdminApi, type TaxCompanyView, type TaxKind, type TaxRateView } from '../../api/client';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, SelectField } from '../../components/ui';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → Billing → Taxes (1.13.0, Teampace 3098): taxes on or off, each company's service
 * address and whether its place is ready, the tax table (drafts to approve, rows in force to retire,
 * a new draft with its official source), exemption certificates, and the month's tax report.
 */

const KIND: Record<TaxKind, string> = { usf: 'USF', e911: '911 fee', telecom: 'Communications tax', sales: 'Sales tax', other: 'Other' };

function placeOf(r: TaxRateView): string {
  if (r.jurisdiction === 'federal') return 'Federal';
  if (r.jurisdiction === 'state') return r.state ?? '';
  if (r.jurisdiction === 'county') return `County ${r.countyFips} (${r.state})`;
  if (r.jurisdiction === 'city') return `Place ${r.placeFips} (${r.state})`;
  return `${r.district} (${r.state})`;
}

function howMuch(r: TaxRateView): string {
  if (r.method === 'percent') return `${r.rate}%${r.share < 1 ? ` on ${Math.round(r.share * 1000) / 10}%` : ''} of ${r.basis === 'voice' ? 'voice' : r.basis === 'nonvoice' ? 'other' : 'all'} charges`;
  return `$${(r.rate / 100).toFixed(2)} per ${r.method === 'per_line' ? 'voice line' : 'number'}`;
}

const err = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function BillingTaxes({ month }: { month: string }) {
  const d = useData(() => taxAdminApi.overview(), [], { live: false });
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [address, setAddress] = useState<TaxCompanyView | null>(null);
  const [exempt, setExempt] = useState<TaxCompanyView | null>(null);
  if (d.error) return <LoadError error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const o = d.data;
  const act = async (fn: () => Promise<unknown>) => {
    setError('');
    try {
      await fn();
      d.reload();
    } catch (e) {
      setError(err(e));
    }
  };
  const drafts = o.rates.filter((r) => r.status === 'draft');
  const live = o.rates.filter((r) => r.status === 'approved');
  const waiting = o.companies.filter((c) => !c.ready);
  return (
    <Card
      title="Taxes"
      action={
        <Chip tone={o.on ? 'ok' : 'warn'} small>
          {o.on ? 'On invoices' : 'Off'}
        </Chip>
      }
    >
      <p className="muted">
        {o.on
          ? `Invoices get their place’s approved rows. ${waiting.length ? `${waiting.length} ${waiting.length === 1 ? 'company waits' : 'companies wait'}: their invoices are held until their place is ready.` : 'Every company’s place is ready.'}`
          : 'Off: invoices have no tax lines. Turn taxes on once every company’s place is ready.'}
      </p>
      <div className="billing-actions">
        <Button variant={o.on ? 'secondary' : 'primary'} onClick={() => void act(() => billingAdminApi.saveSettings({ taxesOn: !o.on }))}>
          {o.on ? 'Turn taxes off' : 'Turn taxes on'}
        </Button>
        <a className="btn secondary" href={taxAdminApi.reportPdfUrl(month)} download>
          Tax report ({month})
        </a>
        <a className="btn ghost" href={taxAdminApi.reportCsvUrl(month)} download>
          CSV
        </a>
        <span className="grow" />
        <Button icon="plus" onClick={() => setAdding(true)}>
          Add a tax rate
        </Button>
      </div>
      {error && <Notice tone="crit">{error}</Notice>}

      <h3 className="section-title">Research by Claude</h3>
      <p className="muted">
        {o.research.on
          ? 'On: each hour, every place a company is in (its state, county and city) and the federal USF is looked into once a quarter, from 15 days before the quarter starts. What Claude finds is added below as drafts, each with its official source, for someone to approve.'
          : 'Off: add rates by hand. Switched on, Claude looks up each place’s taxes once a quarter and adds what it finds as drafts for approval.'}{' '}
        What it sends to the Claude API: only a place’s name and codes (a state, a county or city name and its FIPS code) and the quarter. Never a company, a person, an address or an amount.
        {!o.research.configured && ' It needs a Claude API key (ANTHROPIC_API_KEY), which isn’t set here.'}
      </p>
      <div className="billing-actions">
        <Button variant="secondary" disabled={!o.research.configured} onClick={() => void act(() => billingAdminApi.saveSettings({ taxResearchOn: !o.research.on }))}>
          {o.research.on ? 'Turn research off' : 'Turn research on'}
        </Button>
        {o.research.on && (
          <Button variant="ghost" onClick={() => void act(() => taxAdminApi.runResearch())}>
            Research now
          </Button>
        )}
      </div>
      {o.research.recent.length > 0 && (
        <ul className="plain-list">
          {o.research.recent.slice(0, 8).map((r) => (
            <li key={r.key} className="small">
              <Chip tone={r.status === 'done' ? 'ok' : 'crit'} small>
                {r.status === 'done' ? 'Done' : 'Failed'}
              </Chip>{' '}
              {r.place} for {r.quarter}: {r.said} <span className="muted">{new Date(r.at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}

      <h3 className="section-title">Companies</h3>
      {o.companies.length === 0 ? (
        <Empty title="No billing accounts yet" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Service address</th>
                <th>Place</th>
                <th>Ready</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {o.companies.map((c) => (
                <tr key={c.customerId}>
                  <td>{c.company}</td>
                  <td className="muted">{c.place ? [c.place.address.line1, c.place.address.city, c.place.address.state].filter(Boolean).join(', ') : 'Not set'}</td>
                  <td>{c.place?.matched ? [c.place.city, c.place.county, c.place.state].filter(Boolean).join(', ') : (c.place?.said ?? '')}</td>
                  <td>
                    {c.ready ? (
                      <Chip tone="ok" small>
                        Ready
                      </Chip>
                    ) : (
                      <span className="crit-text small">{c.reason}</span>
                    )}
                    {c.exemptions.length > 0 && <div className="muted small">Exempt: {[...new Set(c.exemptions.flatMap((e) => e.kinds.map((k) => KIND[k])))].join(', ')}</div>}
                  </td>
                  <td className="num">
                    <Button size="small" variant="ghost" onClick={() => setAddress(c)}>
                      Address
                    </Button>
                    <Button size="small" variant="ghost" onClick={() => setExempt(c)}>
                      Exemption
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="section-title">Waiting for approval ({drafts.length})</h3>
      {drafts.length === 0 ? <p className="muted">Nothing waiting.</p> : <RateTable rates={drafts} act={act} />}
      <h3 className="section-title">In force ({live.length})</h3>
      {live.length === 0 ? <p className="muted">No approved rates yet.</p> : <RateTable rates={live} act={act} />}

      {adding && <AddRate onClose={() => setAdding(false)} onSaved={() => (setAdding(false), d.reload())} />}
      {address && <AddressDialog company={address} onClose={() => setAddress(null)} onSaved={() => (setAddress(null), d.reload())} />}
      {exempt && <ExemptionDialog company={exempt} onClose={() => setExempt(null)} onSaved={() => (setExempt(null), d.reload())} act={act} />}
    </Card>
  );
}

function RateTable({ rates, act }: { rates: TaxRateView[]; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Place</th>
            <th>Tax</th>
            <th>Rate</th>
            <th>From</th>
            <th>Added by</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rates.map((r) => (
            <tr key={r.id}>
              <td>{placeOf(r)}</td>
              <td>
                <span className="cell-stack">
                  <strong>{r.name}</strong>
                  <small className="muted">
                    {KIND[r.kind]} ·{' '}
                    <a href={r.sourceUrl} target="_blank" rel="noreferrer noopener">
                      source
                    </a>
                    {r.notes ? ` · ${r.notes}` : ''}
                  </small>
                </span>
              </td>
              <td>{howMuch(r)}</td>
              <td>{r.effectiveFrom}</td>
              <td className="muted">{r.approvedBy ? `${r.createdBy}, approved by ${r.approvedBy}` : r.createdBy}</td>
              <td className="num">
                {r.status === 'draft' ? (
                  <>
                    <Button size="small" variant="primary" onClick={() => void act(() => taxAdminApi.approve(r.id))}>
                      Approve
                    </Button>
                    <Button size="small" variant="ghost" onClick={() => void act(() => taxAdminApi.retire(r.id, r.effectiveFrom))}>
                      Remove
                    </Button>
                  </>
                ) : (
                  <Button
                    size="small"
                    variant="ghost"
                    onClick={() => {
                      const lastDay = window.prompt('Last day this rate applies (YYYY-MM-DD)', new Date().toISOString().slice(0, 10));
                      if (lastDay) void act(() => taxAdminApi.retire(r.id, lastDay));
                    }}
                  >
                    Retire
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddRate({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ jurisdiction: 'state', state: '', countyFips: '', placeFips: '', district: '', name: '', kind: 'telecom', method: 'percent', rate: '', share: '1', basis: 'voice', effectiveFrom: '', sourceUrl: '', notes: '' });
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setError('');
    try {
      await taxAdminApi.addRate({
        jurisdiction: f.jurisdiction as TaxRateView['jurisdiction'],
        state: f.jurisdiction === 'federal' ? null : f.state.trim() || null,
        countyFips: f.countyFips.trim() || null,
        placeFips: f.placeFips.trim() || null,
        district: f.district.trim() || null,
        name: f.name.trim(),
        kind: f.kind as TaxKind,
        method: f.method as TaxRateView['method'],
        rate: f.method === 'percent' ? Number(f.rate) : Math.round(Number(f.rate) * 100),
        share: Number(f.share) || 1,
        basis: f.basis as TaxRateView['basis'],
        effectiveFrom: f.effectiveFrom,
        sourceUrl: f.sourceUrl.trim(),
        notes: f.notes.trim()
      });
      onSaved();
    } catch (e) {
      setError(err(e));
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a tax rate"
      subtitle="It waits for someone else on the Stitch team to approve it before it’s on any invoice."
      foot={
        <>
          <span className="grow" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()}>
            Add for approval
          </Button>
        </>
      }
    >
      <SelectField label="Where" value={f.jurisdiction} onChange={set('jurisdiction')} options={[{ value: 'federal', label: 'Federal' }, { value: 'state', label: 'A state' }, { value: 'county', label: 'A county' }, { value: 'city', label: 'A city' }, { value: 'district', label: 'A district' }]} />
      {f.jurisdiction !== 'federal' && <Field label="State (two letters)" value={f.state} onChange={set('state')} maxLength={2} />}
      {f.jurisdiction === 'county' && <Field label="County FIPS (5 digits)" value={f.countyFips} onChange={set('countyFips')} inputMode="numeric" />}
      {f.jurisdiction === 'city' && <Field label="Place FIPS (7 digits)" value={f.placeFips} onChange={set('placeFips')} inputMode="numeric" />}
      {f.jurisdiction === 'district' && <Field label="District name" value={f.district} onChange={set('district')} />}
      <Field label="Name on invoices" value={f.name} onChange={set('name')} placeholder="911 fee · Maryland" />
      <div className="billing-fields">
        <SelectField label="Kind" value={f.kind} onChange={set('kind')} options={(Object.keys(KIND) as TaxKind[]).map((k) => ({ value: k, label: KIND[k] }))} />
        <SelectField label="Charged" value={f.method} onChange={set('method')} options={[{ value: 'percent', label: 'Percent' }, { value: 'per_line', label: 'Per voice line' }, { value: 'per_number', label: 'Per number' }]} />
        <Field label={f.method === 'percent' ? 'Rate (%)' : 'Amount ($)'} value={f.rate} onChange={set('rate')} inputMode="decimal" />
      </div>
      {f.method === 'percent' && (
        <div className="billing-fields">
          <SelectField label="On" value={f.basis} onChange={set('basis')} options={[{ value: 'voice', label: 'Voice charges' }, { value: 'nonvoice', label: 'Other charges' }, { value: 'both', label: 'All charges' }]} />
          <Field label="Share of the base (0–1)" value={f.share} onChange={set('share')} inputMode="decimal" hint="USF: 0.649, the interstate safe harbor" />
        </div>
      )}
      <Field label="From (YYYY-MM-DD)" value={f.effectiveFrom} onChange={set('effectiveFrom')} placeholder="2027-01-01" />
      <Field label="Official source (link)" value={f.sourceUrl} onChange={set('sourceUrl')} placeholder="https://…" />
      <Field label="Notes (optional)" value={f.notes} onChange={set('notes')} />
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

function AddressDialog({ company, onClose, onSaved }: { company: TaxCompanyView; onClose: () => void; onSaved: () => void }) {
  const a0 = company.place?.address ?? {};
  const [a, setA] = useState({ line1: a0.line1 ?? '', city: a0.city ?? '', state: a0.state ?? '', zip: a0.zip ?? '' });
  const [said, setSaid] = useState('');
  const [error, setError] = useState('');
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${company.company}: service address`}
      subtitle="Where the service is, for taxes (not the billing address). Looked up with the US Census geocoder."
      foot={
        <>
          <span className="grow" />
          <Button variant="ghost" onClick={said ? onSaved : onClose}>
            {said ? 'Done' : 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              setError('');
              try {
                const r = await taxAdminApi.serviceAddress(company.customerId, a);
                setSaid(r.ready ? 'Found, and its place is ready.' : `Saved. ${r.reason}`);
              } catch (e) {
                setError(err(e));
              }
            }}
          >
            Look it up
          </Button>
        </>
      }
    >
      <Field label="Street address" value={a.line1} onChange={(e) => setA({ ...a, line1: e.target.value })} />
      <div className="billing-fields">
        <Field label="City" value={a.city} onChange={(e) => setA({ ...a, city: e.target.value })} />
        <Field label="State" value={a.state} onChange={(e) => setA({ ...a, state: e.target.value })} maxLength={2} />
        <Field label="ZIP" value={a.zip} onChange={(e) => setA({ ...a, zip: e.target.value })} inputMode="numeric" />
      </div>
      {said && <Notice tone="ok">{said}</Notice>}
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

function ExemptionDialog({ company, onClose, onSaved, act }: { company: TaxCompanyView; onClose: () => void; onSaved: () => void; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [kinds, setKinds] = useState<TaxKind[]>([]);
  const [expiresOn, setExpires] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const save = async () => {
    setError('');
    if (!file) return setError('Choose the certificate (PDF or a picture).');
    try {
      const content = btoa(Array.from(new Uint8Array(await file.arrayBuffer()), (b) => String.fromCharCode(b)).join(''));
      await taxAdminApi.addExemption(company.customerId, { kinds, expiresOn: expiresOn || null, fileName: file.name, contentType: file.type || 'application/pdf', content });
      onSaved();
    } catch (e) {
      setError(err(e));
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${company.company}: tax exemption`}
      subtitle="The company is charged none of the kinds of tax its certificate covers, until it expires."
      foot={
        <>
          <span className="grow" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!kinds.length || !file} onClick={() => void save()}>
            Add certificate
          </Button>
        </>
      }
    >
      {company.exemptions.length > 0 && (
        <ul className="plain-list">
          {company.exemptions.map((e) => (
            <li key={e.id}>
              <a href={taxAdminApi.exemptionUrl(e.id)} download>
                {e.fileName}
              </a>{' '}
              <span className="muted small">
                {e.kinds.map((k) => KIND[k]).join(', ')}
                {e.expiresOn ? `, until ${e.expiresOn}` : ''}
              </span>{' '}
              <Button size="small" variant="ghost" onClick={() => void act(() => taxAdminApi.revokeExemption(e.id)).then(onSaved)}>
                End it
              </Button>
            </li>
          ))}
        </ul>
      )}
      <fieldset className="plain-fieldset">
        <legend className="field-label">Exempt from</legend>
        <div className="check-list">
          {(Object.keys(KIND) as TaxKind[]).map((k) => (
            <label key={k} className="check-row">
              <input type="checkbox" checked={kinds.includes(k)} onChange={() => setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k])} />
              <span>{KIND[k]}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <Field label="Expires (YYYY-MM-DD, optional)" value={expiresOn} onChange={(e) => setExpires(e.target.value)} />
      <Field label="Certificate" type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}
